/**
 * Generic API investigation collector.
 *
 * Handles API-type collection tasks (MarineTraffic, custom endpoints, etc.).
 * Routes requests through the sidecar to keep API keys server-side.
 *
 * For APIs that require authentication, the sidecar holds the credentials
 * and proxies the request. This collector constructs the appropriate
 * sidecar proxy URL based on the source configuration.
 */

import { getApiBase } from '@/app/api-router';
import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface APICollectionResult {
  rawText: string;
  recordCount: number;
  endpoint: string;
  responseStatus: number;
}

/** Default timeout for API queries (ms). */
const DEFAULT_TIMEOUT_MS = 30_000;

// ---------------------------------------------------------------------------
// Known API endpoint mappers
// ---------------------------------------------------------------------------

/**
 * Map of known source ID patterns to sidecar proxy routes.
 * The sidecar holds API keys and proxies requests.
 */
const SIDECAR_ROUTES: Record<string, string> = {
  marinetraffic: '/api/data/vessels',
  ais: '/api/data/ais-stream',
  opensky: '/api/data/opensky',
  vessels: '/api/data/vessels',
};

/**
 * Determine the sidecar proxy route for a given source ID.
 * Returns null if no known proxy exists (direct API call needed).
 */
function getSidecarRoute(sourceId: string): string | null {
  const id = sourceId.toLowerCase();
  for (const [pattern, route] of Object.entries(SIDECAR_ROUTES)) {
    if (id.includes(pattern)) return route;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Query parameter builder
// ---------------------------------------------------------------------------

/**
 * Build query parameters from the collection task for API requests.
 * Extracts geographic bounds, temporal bounds, and specific filters.
 */
function buildQueryParams(task: CollectionTask): URLSearchParams {
  const params = new URLSearchParams();
  const query = task.query as Record<string, unknown>;

  // Geographic bounds → bounding box params
  const geo = query.geographicBounds as { bbox?: number[] } | undefined;
  if (geo?.bbox && geo.bbox.length === 4) {
    const [south, west, north, east] = geo.bbox;
    params.set('lamin', String(south));
    params.set('lomin', String(west));
    params.set('lamax', String(north));
    params.set('lomax', String(east));
  }

  // Temporal bounds
  const temporal = query.temporalBounds as { start?: string; end?: string } | undefined;
  if (temporal?.start) {
    params.set('start', temporal.start);
  }
  if (temporal?.end) {
    params.set('end', temporal.end);
  }

  // Keywords
  const keywords = query.keywords as string[] | undefined;
  if (keywords && keywords.length > 0) {
    params.set('q', keywords.join(','));
  }

  return params;
}

// ---------------------------------------------------------------------------
// Response normalizer
// ---------------------------------------------------------------------------

/**
 * Normalize API response data to text suitable for entity extraction.
 * Handles JSON responses with various shapes.
 */
function normalizeResponseToText(data: unknown, sourceId: string): { text: string; recordCount: number } {
  if (typeof data === 'string') {
    return { text: data, recordCount: 1 };
  }

  if (Array.isArray(data)) {
    const items = data.slice(0, 100); // Cap at 100 records
    const textParts: string[] = [];

    for (const item of items) {
      if (typeof item === 'string') {
        textParts.push(item);
      } else if (typeof item === 'object' && item !== null) {
        textParts.push(formatObjectAsText(item as Record<string, unknown>));
      }
    }

    return {
      text: textParts.join('\n\n---\n\n') || `No records from ${sourceId}.`,
      recordCount: items.length,
    };
  }

  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;

    // Common response shapes: { data: [...] }, { results: [...] }, { items: [...] }
    for (const key of ['data', 'results', 'items', 'records', 'articles', 'states']) {
      if (Array.isArray(obj[key])) {
        return normalizeResponseToText(obj[key], sourceId);
      }
    }

    // Single object response
    return {
      text: formatObjectAsText(obj),
      recordCount: 1,
    };
  }

  return { text: `No parseable data from ${sourceId}.`, recordCount: 0 };
}

/**
 * Format a JSON object as human-readable text for entity extraction.
 */
function formatObjectAsText(obj: Record<string, unknown>): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) continue;

    if (typeof value === 'object') {
      // Skip deeply nested objects — flatten one level
      parts.push(`${key}: ${JSON.stringify(value)}`);
    } else {
      parts.push(`${key}: ${String(value)}`);
    }
  }

  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Collector
// ---------------------------------------------------------------------------

/**
 * Fetch data from an API endpoint for a collection task.
 * Routes through the sidecar proxy when a known mapping exists.
 * Returns text content suitable for entity extraction.
 */
export async function collectFromAPI(
  task: CollectionTask,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<APICollectionResult> {
  const query = task.query as Record<string, unknown>;
  const endpoint = (query.endpoint as string) ?? '';

  // Determine if we use a sidecar proxy route
  const sidecarRoute = getSidecarRoute(task.sourceId);
  const queryParams = buildQueryParams(task);

  let fetchUrl: string;
  if (sidecarRoute) {
    // Route through sidecar — API keys are server-side
    const paramStr = queryParams.toString();
    fetchUrl = `${getApiBase()}${sidecarRoute}${paramStr ? '?' + paramStr : ''}`;
  } else if (endpoint) {
    // Direct API call (no auth required, or auth handled differently)
    const paramStr = queryParams.toString();
    fetchUrl = paramStr ? `${endpoint}?${paramStr}` : endpoint;
  } else {
    throw new Error(`API task for source ${task.sourceId} has no endpoint and no known proxy`);
  }

  const response = await globalThis.fetch(fetchUrl, {
    headers: {
      'User-Agent': 'Sentinel/1.0 OSINT Platform',
      Accept: 'application/json',
    },
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!response.ok) {
    throw new Error(
      `API request failed for ${task.sourceId}: ${response.status} ${response.statusText}`,
    );
  }

  // Parse response
  let data: unknown;
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('json')) {
    data = await response.json();
  } else {
    // Treat as text (CSV, XML, etc.)
    data = await response.text();
  }

  const { text, recordCount } = normalizeResponseToText(data, task.sourceId);

  return {
    rawText: text,
    recordCount,
    endpoint: sidecarRoute ?? endpoint,
    responseStatus: response.status,
  };
}
